/**
 * Size Picker Broker
 * - Clicking a card's "+" moves THAT card's .product-sizes-wrapper to <body> (portal)
 *   and restores it to its original place on close.
 * - No visual styles are applied from JS. Only position data is exposed as CSS variables
 *   on the wrapper (--spb-left, --spb-top, --spb-width) for use in theme CSS.
 * - The "+" area is hit-tested by coordinates, so the card's overlay link
 *   no longer loads the product page when the "+" is clicked.
 * - Size clicks are NOT handled here; existing size handlers keep working
 *   (listeners stay attached when an element is moved in the DOM).
 */
(function () {
  if (window.SizePickerBroker) return; // Guard against double include

  /* ---------------- Broker (pub/sub) ---------------- */
  const listeners = {};
  const broker = {
    on(evt, fn) {
      (listeners[evt] = listeners[evt] || []).push(fn);
      return () => broker.off(evt, fn);
    },
    off(evt, fn) {
      listeners[evt] = (listeners[evt] || []).filter((f) => f !== fn);
    },
    emit(evt, payload) {
      (listeners[evt] || []).forEach((fn) => {
        try { fn(payload); } catch (e) { console.error('[SizePickerBroker]', evt, e); }
      });
      // Also expose as DOM events so any theme/app script can listen without the global
      document.dispatchEvent(new CustomEvent('sizepicker:' + evt, { detail: payload }));
    }
  };

  /* ---------------- Selectors / config ---------------- */
  const SEL = {
    card: '.card-wrapper',
    anchor: '.card__inner',
    plus: '.mobile_arrow-card',
    wrapper: '.product-sizes-wrapper'
  };

  // Extra clickable margin around the "+" so near-misses don't open the product page
  const HIT_PADDING = 8;

  // CSS variables written on the portaled wrapper (position data only, no styling)
  const VARS = ['--spb-left', '--spb-top', '--spb-width'];

  // { wrapper, placeholder, card, plus }
  let active = null;

  /* ---------------- Portal ---------------- */
  // Exposes the card image's page coordinates to CSS; styling is left to theme CSS
  function position() {
    if (!active) return;
    const anchor = active.card.querySelector(SEL.anchor) || active.card;
    const r = anchor.getBoundingClientRect();
    const s = active.wrapper.style;

    s.setProperty('--spb-left', r.left + window.scrollX + 'px');
    s.setProperty('--spb-top', r.bottom + window.scrollY + 'px'); // Bottom edge of card image
    s.setProperty('--spb-width', r.width + 'px');
  }

  function open(card, plus) {
    // Toggle: same card clicked again -> close
    if (active && active.card === card) return close();
    close();

    const wrapper = card.querySelector(SEL.wrapper);
    if (!wrapper) return;

    // Placeholder marks the original spot so we can restore it exactly
    const placeholder = document.createComment('size-wrapper-placeholder');
    wrapper.parentNode.insertBefore(placeholder, wrapper);

    active = { wrapper, placeholder, card, plus };

    document.body.appendChild(wrapper);
    wrapper.classList.add('is-portaled');
    card.classList.add('sizes-open');
    if (plus) plus.setAttribute('aria-expanded', 'true');

    position();
    broker.emit('open', { productId: Number(wrapper.dataset.productId), wrapper, card });
  }

  function close() {
    if (!active) return;
    const { wrapper, placeholder, card, plus } = active;
    active = null;

    wrapper.classList.remove('is-portaled');
    VARS.forEach((v) => wrapper.style.removeProperty(v));
    if (!wrapper.getAttribute('style')) wrapper.removeAttribute('style');

    if (placeholder.isConnected) {
      placeholder.replaceWith(wrapper);
    } else {
      // Card was re-rendered (filters / infinite scroll) -> drop the orphan
      wrapper.remove();
    }

    card.classList.remove('sizes-open');
    if (plus) plus.setAttribute('aria-expanded', 'false');
    broker.emit('close', { productId: Number(wrapper.dataset.productId) });
  }

  /* ---------------- Plus hit-testing ---------------- */
  // Returns the "+" element if the click landed on it, even when the overlay link sits on top
  function findPlusAtPoint(e) {
    const direct = e.target.closest(SEL.plus);
    if (direct) return direct;

    const card = e.target.closest(SEL.card);
    if (!card) return null;

    const plus = card.querySelector(SEL.plus);
    if (!plus) return null;

    const r = plus.getBoundingClientRect();
    if (!r.width || !r.height) return null; // Hidden (e.g. desktop) -> ignore

    const x = e.clientX;
    const y = e.clientY;
    const inside =
      x >= r.left - HIT_PADDING &&
      x <= r.right + HIT_PADDING &&
      y >= r.top - HIT_PADDING &&
      y <= r.bottom + HIT_PADDING;

    return inside ? plus : null;
  }

  /* ---------------- Delegated events ---------------- */
  // Capture phase: runs before the overlay link navigates and before theme/quickview handlers
  document.addEventListener('click', function (e) {
    const plus = findPlusAtPoint(e);
    if (plus) {
      const card = plus.closest(SEL.card);
      if (!card) return;
      e.preventDefault();            // Stop the overlay link from loading the product page
      e.stopPropagation();           // Stop card / quickview click handlers
      e.stopImmediatePropagation();
      open(card, plus);
      return;
    }

    // Outside click closes the picker; clicks inside the wrapper (sizes) pass through untouched
    if (active && !active.wrapper.contains(e.target)) close();
  }, true);

  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  window.addEventListener('resize', position);
  window.addEventListener('load', position); // Lazy images can shift layout

  window.SizePickerBroker = { on: broker.on, off: broker.off, emit: broker.emit, open, close };
})();